import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Spin,
  message,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  Empty,
  Descriptions,
  Tag,
  Modal,
  Input,
} from 'antd';
import {
  ArrowLeftOutlined,
  SafetyOutlined,
  MailOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  StopOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useState, useMemo } from 'react';
import { useWisecureQuery, useWisecureMutation, useInvalidateQueries } from '@wisecure/api-client/hooks';
import type { AgentResponse, DocumentResponse } from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { CopyIdButton } from '@/components/CopyIdButton';
import { ConfirmDeleteButton } from '@/components/ConfirmDeleteButton';
import { DocumentList } from '@/components/DocumentList';
import { StatusBadge, WisecureCard } from '@/components/base';
import { neutral, primary, success, error, warning } from '@/theme';

const { Text, Title } = Typography;

interface PaginatedDocuments {
  data: DocumentResponse[];
  total: number;
}

const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  AWAITING_REGISTRATION: { color: warning[300], icon: <ClockCircleOutlined />, label: 'En attente d\'inscription' },
  DOCUMENTS_PENDING: { color: warning[400], icon: <ClockCircleOutlined />, label: 'Documents en attente' },
  OPERATIONAL: { color: success[400], icon: <CheckCircleOutlined />, label: 'Opérationnel' },
  SUSPENDED: { color: error[400], icon: <CloseCircleOutlined />, label: 'Suspendu' },
};
const fallbackStatus = { color: warning[400], icon: <ClockCircleOutlined />, label: 'En attente' };

const docStatusIcon = (s: string) => {
  if (s === 'VALIDATED') return <CheckCircleOutlined style={{ color: success[400] }} />;
  if (s === 'DENIED') return <CloseCircleOutlined style={{ color: error[400] }} />;
  return <ClockCircleOutlined style={{ color: warning[400] }} />;
};

export function AgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidateQueries();

  const { data: agent, isLoading } = useWisecureQuery<AgentResponse>(
    queryKeys.agentDetail(id!),
    (client) => client.agents.agentsControllerFindOne({ id: id! }),
    { enabled: !!id },
  );

  const { data: docsData, isLoading: docsLoading } = useWisecureQuery<PaginatedDocuments>(
    queryKeys.documents('agent_profile', id!),
    (client) =>
      client.documents.documentsControllerFindAll({
        entityType: 'agent_profile',
        entityId: id,
      }) as Promise<PaginatedDocuments>,
    { enabled: !!id },
  );

  const deleteMutation = useWisecureMutation(
    (client) => client.agents.agentsControllerRemove({ id: id! }),
    {
      onSuccess: () => {
        message.success('Agent supprimé');
        invalidate(queryKeys.dashboardPendingValidations);
        navigate('/users/validation');
      },
      onError: () => message.error('Erreur lors de la suppression de l\'agent'),
    },
  );

  const suspendMutation = useWisecureMutation(
    (client) => client.agents.agentsControllerSuspend({ id: id! }),
    {
      onSuccess: () => {
        message.success('Agent suspendu');
        invalidate(queryKeys.agentDetail(id!));
      },
      onError: () => message.error('Erreur lors de la suspension de l\'agent'),
    },
  );

  const unsuspendMutation = useWisecureMutation(
    (client) => client.agents.agentsControllerUnsuspend({ id: id! }),
    {
      onSuccess: () => {
        message.success('Agent réactivé');
        invalidate(queryKeys.agentDetail(id!));
      },
      onError: () => message.error('Erreur lors de la réactivation de l\'agent'),
    },
  );

  const refuseDocMutation = useWisecureMutation(
    (client, { documentType, reason }: { documentType: 'PRO_CARD' | 'ID_CARD'; reason?: string }) =>
      client.agents.agentsControllerRefuseDocument({ id: id!, requestBody: { documentType, reason } }),
    {
      onSuccess: () => {
        message.success('Document refusé');
        invalidate(queryKeys.agentDetail(id!));
        invalidate(queryKeys.documents('agent_profile', id!));
      },
      onError: () => message.error('Erreur lors du refus du document'),
    },
  );

  const validationLinkMutation = useWisecureMutation(
    (client) => client.agents.agentsControllerGetValidationLink({ id: id! }),
    {
      onSuccess: (data) => {
        if (data.validationLink) {
          navigator.clipboard.writeText(data.validationLink);
          message.success('Lien de validation copié');
        }
      },
      onError: () => message.error('Erreur lors de la récupération du lien'),
    },
  );

  const [refuseDocModalOpen, setRefuseDocModalOpen] = useState(false);
  const [refuseDocType, setRefuseDocType] = useState<'PRO_CARD' | 'ID_CARD'>('PRO_CARD');
  const [refuseDocReason, setRefuseDocReason] = useState('');

  const docs = useMemo(() => docsData?.data ?? [], [docsData]);
  const pendingDocs = useMemo(() => docs.filter((d) => d.status === 'PENDING'), [docs]);
  const validatedDocs = useMemo(() => docs.filter((d) => d.status === 'VALIDATED'), [docs]);
  const deniedDocs = useMemo(() => docs.filter((d) => d.status === 'DENIED'), [docs]);

  const reviewStatusLabel = (() => {
    if (pendingDocs.length > 0) return { text: 'Documents en attente', color: warning[400], icon: <ClockCircleOutlined /> };
    if (deniedDocs.length > 0 && validatedDocs.length === 0) return { text: 'Documents refusés', color: error[400], icon: <CloseCircleOutlined /> };
    if (deniedDocs.length > 0) return { text: 'Certains documents refusés', color: warning[300], icon: <InfoCircleOutlined /> };
    if (validatedDocs.length > 0) return { text: 'Tous les documents validés', color: success[400], icon: <CheckCircleOutlined /> };
    return { text: 'Aucun document', color: neutral[500], icon: <FileTextOutlined /> };
  })();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Empty description="Agent non trouvé">
          <Button onClick={() => navigate('/users/validation')}>Retour à la liste</Button>
        </Empty>
      </div>
    );
  }

  const status = statusConfig[agent.status] ?? fallbackStatus;
  const docsCount = docsData?.total ?? 0;

  return (
    <div style={{ maxWidth: 1200 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ color: neutral[500] }}
        />
        <Text style={{ color: neutral[300], fontSize: 13 }}>Agents</Text>
        <Text style={{ color: neutral[200] }}>/</Text>
        <Text style={{ color: neutral[700], fontWeight: 500, fontSize: 13 }}>
          {agent.firstName} {agent.lastName}
        </Text>
      </div>

      <WisecureCard
        dsVariant="highlight"
        style={{ marginBottom: 24, padding: 0 }}
        styles={{ body: { padding: '28px 32px' } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: primary[600],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SafetyOutlined style={{ fontSize: 24, color: neutral[0] }} />
            </div>
            <div>
              <Title level={3} style={{ margin: 0, color: neutral[0], fontWeight: 700, letterSpacing: '-0.5px' }}>
                {agent.firstName} {agent.lastName}
              </Title>
              <Space size={8} style={{ marginTop: 6 }} wrap>
                <Tag style={{ borderRadius: 100 }}>{agent.societyName}</Tag>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: `${status.color}20`,
                    color: status.color,
                    borderRadius: 100,
                    padding: '3px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {status.icon}
                  {status.label}
                </div>
              </Space>
            </div>
          </div>

          <Space size={8}>
            <CopyIdButton
              id={agent.id}
              type="primary"
              ghost
              size="small"
            />
            <ConfirmDeleteButton
              onConfirm={() => deleteMutation.mutate()}
              loading={deleteMutation.isPending}
              type="primary"
              ghost
              danger
              size="small"
            >
              Supprimer
            </ConfirmDeleteButton>
          </Space>
        </div>

        <Divider style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '20px 0 16px' }} />

        <Row gutter={24}>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Documents</Text>
              <Text style={{ color: neutral[0], fontSize: 22, fontWeight: 700 }}>{docsCount}</Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Carte professionnelle</Text>
              <Text style={{ color: neutral[0], fontSize: 16, fontWeight: 500 }}>
                <Space size={4}>{docStatusIcon(agent.proCardStatus)} {agent.proCardStatus}</Space>
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Carte d'identité</Text>
              <Text style={{ color: neutral[0], fontSize: 16, fontWeight: 500 }}>
                <Space size={4}>{docStatusIcon(agent.idCardStatus)} {agent.idCardStatus}</Space>
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Créé le</Text>
              <Text style={{ color: neutral[0], fontSize: 14, fontWeight: 500 }}>
                {new Date(agent.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
              </Text>
            </div>
          </Col>
        </Row>
      </WisecureCard>

      <Row gutter={24}>
        <Col xs={24} lg={14}>
          <WisecureCard title="Informations" style={{ marginBottom: 24 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={<><MailOutlined style={{ marginRight: 6 }} />Email</>}>
                {agent.email ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 6 }} />Téléphone</>}>
                {agent.phone?.fullNumber ? String(agent.phone.fullNumber) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Date de naissance">
                {agent.birthDate
                  ? new Date(agent.birthDate).toLocaleDateString('fr-FR')
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <StatusBadge status={agent.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Société">
                <Tag style={{ borderRadius: 100 }}>{agent.societyName}</Tag>
              </Descriptions.Item>
              {agent.userId && (
                <Descriptions.Item label="User ID">
                  <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>{agent.userId}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </WisecureCard>
        </Col>

        <Col xs={24} lg={10}>
          <WisecureCard
            title={
              <Space>
                <CheckCircleOutlined style={{ color: primary[400] }} />
                <span>Vérification</span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <div
              style={{
                background: `${reviewStatusLabel.color}08`,
                border: `1px solid ${reviewStatusLabel.color}30`,
                borderRadius: 12,
                padding: '16px 20px',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: `${reviewStatusLabel.color}18`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  color: reviewStatusLabel.color,
                }}
              >
                {reviewStatusLabel.icon}
              </div>
              <div>
                <Text style={{ fontSize: 12, color: neutral[300], display: 'block' }}>Statut des documents</Text>
                <Text style={{ fontWeight: 700, fontSize: 16, color: reviewStatusLabel.color }}>{reviewStatusLabel.text}</Text>
              </div>
            </div>

            <Row gutter={12} style={{ marginBottom: 20 }}>
              <Col span={8}>
                <div
                  style={{
                    textAlign: 'center',
                    background: `${warning[400]}08`,
                    border: `1px solid ${warning[400]}20`,
                    borderRadius: 10,
                    padding: '10px 8px',
                  }}
                >
                  <Text style={{ color: warning[400], fontSize: 20, fontWeight: 700, display: 'block' }}>{pendingDocs.length}</Text>
                  <Text style={{ color: neutral[300], fontSize: 11 }}>En attente</Text>
                </div>
              </Col>
              <Col span={8}>
                <div
                  style={{
                    textAlign: 'center',
                    background: `${success[400]}08`,
                    border: `1px solid ${success[400]}20`,
                    borderRadius: 10,
                    padding: '10px 8px',
                  }}
                >
                  <Text style={{ color: success[400], fontSize: 20, fontWeight: 700, display: 'block' }}>{validatedDocs.length}</Text>
                  <Text style={{ color: neutral[300], fontSize: 11 }}>Validés</Text>
                </div>
              </Col>
              <Col span={8}>
                <div
                  style={{
                    textAlign: 'center',
                    background: `${error[400]}08`,
                    border: `1px solid ${error[400]}20`,
                    borderRadius: 10,
                    padding: '10px 8px',
                  }}
                >
                  <Text style={{ color: error[400], fontSize: 20, fontWeight: 700, display: 'block' }}>{deniedDocs.length}</Text>
                  <Text style={{ color: neutral[300], fontSize: 11 }}>Refusés</Text>
                </div>
              </Col>
            </Row>

            {pendingDocs.length > 0 && (
              <>
                <Text style={{ color: neutral[500], fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 10 }}>
                  Actions requises
                </Text>
                <div
                  style={{
                    background: `${warning[400]}08`,
                    border: `1px solid ${warning[400]}20`,
                    borderRadius: 10,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <ExclamationCircleOutlined style={{ color: warning[400], fontSize: 16, flexShrink: 0 }} />
                  <Text style={{ color: neutral[500], fontSize: 13 }}>
                    Veuillez examiner chaque document individuellement avant de valider l'agent.
                  </Text>
                </div>
              </>
            )}
            {pendingDocs.length === 0 && validatedDocs.length > 0 && (
              <div
                style={{
                  background: `${success[400]}08`,
                  border: `1px solid ${success[400]}20`,
                  borderRadius: 10,
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <CheckCircleOutlined style={{ color: success[400], fontSize: 16, flexShrink: 0 }} />
                <Text style={{ color: success[400], fontSize: 13, fontWeight: 600 }}>
                  Tous les documents ont été examinés
                </Text>
              </div>
            )}
          </WisecureCard>

          <WisecureCard
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Documents</span>
                {docsCount > 0 && (
                  <Text style={{ color: neutral[300], fontSize: 13, fontWeight: 400 }}>{docsCount}</Text>
                )}
              </div>
            }
          >
            <DocumentList
              documents={((docsData?.data ?? []) as unknown as import('@/components/DocumentList').DocumentItem[])}
              isLoading={docsLoading}
              entityType="agent_profile"
              entityId={id!}
              onValidated={() => {
                invalidate(queryKeys.agentDetail(id!));
                invalidate(queryKeys.dashboardPendingValidations);
              }}
            />
          </WisecureCard>

          <WisecureCard
            title={
              <Space>
                <SafetyOutlined style={{ color: primary[400] }} />
                <span>Actions</span>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              {agent.status === 'SUSPENDED' ? (
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  block
                  onClick={() => unsuspendMutation.mutate()}
                  loading={unsuspendMutation.isPending}
                  style={{ height: 44, fontWeight: 600 }}
                >
                  Réactiver l'agent
                </Button>
              ) : (
                <Button
                  danger
                  icon={<StopOutlined />}
                  block
                  onClick={() => suspendMutation.mutate()}
                  loading={suspendMutation.isPending}
                  disabled={agent.status === 'AWAITING_REGISTRATION'}
                  style={{ height: 44, fontWeight: 600 }}
                >
                  Suspendre l'agent
                </Button>
              )}

              {agent.status === 'AWAITING_REGISTRATION' && (
                <Button
                  icon={<LinkOutlined />}
                  block
                  onClick={() => validationLinkMutation.mutate()}
                  loading={validationLinkMutation.isPending}
                  style={{ height: 44, fontWeight: 600 }}
                >
                  Copier le lien d'inscription
                </Button>
              )}

              {(agent.proCardStatus === 'PENDING' || agent.idCardStatus === 'PENDING') && (
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  block
                  onClick={() => {
                    if (agent.proCardStatus === 'PENDING' && agent.idCardStatus === 'PENDING') {
                      setRefuseDocType('PRO_CARD');
                    } else if (agent.proCardStatus === 'PENDING') {
                      setRefuseDocType('PRO_CARD');
                    } else {
                      setRefuseDocType('ID_CARD');
                    }
                    setRefuseDocReason('');
                    setRefuseDocModalOpen(true);
                  }}
                  style={{ height: 44, fontWeight: 600 }}
                >
                  Refuser un document
                </Button>
              )}
            </Space>
          </WisecureCard>
        </Col>
      </Row>

      <Modal
        title="Refuser un document"
        open={refuseDocModalOpen}
        onOk={() => {
          refuseDocMutation.mutate({ documentType: refuseDocType, reason: refuseDocReason || undefined });
          setRefuseDocModalOpen(false);
        }}
        onCancel={() => {
          setRefuseDocModalOpen(false);
          setRefuseDocReason('');
        }}
        okText="Refuser"
        cancelText="Annuler"
        okButtonProps={{ danger: true, loading: refuseDocMutation.isPending, disabled: !refuseDocReason.trim() }}
      >
        <div style={{ marginBottom: 12 }}>
          <Text style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Document</Text>
          <Space>
            <Tag
              color={refuseDocType === 'PRO_CARD' ? 'error' : undefined}
              style={{ cursor: 'pointer' }}
              onClick={() => setRefuseDocType('PRO_CARD')}
            >
              Carte pro
            </Tag>
            <Tag
              color={refuseDocType === 'ID_CARD' ? 'error' : undefined}
              style={{ cursor: 'pointer' }}
              onClick={() => setRefuseDocType('ID_CARD')}
            >
              Carte d'identité
            </Tag>
          </Space>
        </div>
        <Input.TextArea
          rows={3}
          placeholder="Raison du refus (obligatoire)..."
          value={refuseDocReason}
          onChange={(e) => setRefuseDocReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
